/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import type { WarningModuleStore } from '../types';
import type { WarningFormState } from './reducer';

const EMPTY_WARNING_FORM_STATE: WarningFormState = {};

export const selectWarningsFormState = (
  store: WarningModuleStore,
): WarningFormState => {
  if (store?.warningsForm) {
    return store.warningsForm;
  }
  return EMPTY_WARNING_FORM_STATE;
};

export const getPublicWarning = createSelector(
  selectWarningsFormState,
  (store) => store?.warning,
);

export const getPublicWarningStatus = createSelector(
  selectWarningsFormState,
  (store) => store?.warning?.status,
);

export const getPublicWarningId = createSelector(
  selectWarningsFormState,
  (store) => store?.warning?.warningDetail.id,
);

export const getPublicWarningAreas = createSelector(
  selectWarningsFormState,
  (store) => store?.warning?.warningDetail.areas || [],
);

export const getPublicWarningFormState = createSelector(
  selectWarningsFormState,
  (store) => store?.formState || '',
);

export const getSelectedWarningId = createSelector(
  selectWarningsFormState,
  (store) => store?.selectedWarningId || '',
);

export const getSelectedPublicIsFormDirty = createSelector(
  selectWarningsFormState,
  (store) => store?.isFormDirty || false,
);

export const getWarningType = createSelector(
  selectWarningsFormState,
  (store) => store?.warningType,
);

export const getPublicWarningPanelId = createSelector(
  selectWarningsFormState,
  (store) => store?.panelId || '',
);
